import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { Paperclip, Award, IndianRupee } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, StatusBadge, EmptyState } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { PaginationBar, usePagedList, CardListSkeleton as ListSkeleton } from "../../components/PaginationBar";
import { FetchButton, FetchDirtyHint, FetchForm, FetchPrompt, useFetchGate } from "../../components/FetchBar";
import { api, errMsg, fmtDate, fmtINR, fileHref, downloadBlob } from "../../lib/api";

const APPLICATION_STATUSES = ["submitted", "in_progress", "completed", "rejected", "cancelled"];

export default function ApplicationsAdmin() {
  const { code } = useParams();
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  // Phase 133: the list loads only on Fetch, with the search / status applied at that moment
  const gate = useFetchGate({ q: q.trim(), status: statusFilter });
  const search = gate.applied?.q || "";
  const appliedStatus = gate.applied?.status || "all";
  const list = usePagedList(`/t/${code}/applications`, {
    params: { search: search || undefined, status: appliedStatus === "all" ? undefined : appliedStatus },
    enabled: gate.fetched, reloadKey: gate.version,
  });
  const items = list.data ? list.items : null;
  const clearAll = () => { setQ(""); setStatusFilter("all"); gate.reset(); };
  const [selected, setSelected] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [feeMode, setFeeMode] = useState("Cash");
  const [feeRef, setFeeRef] = useState("");
  const [busy, setBusy] = useState(false);

  const load = list.reload;

  const refreshSelected = async (id) => {
    await list.reload();
    const r = await api.get(`/t/${code}/applications`, { params: { page: list.page, limit: list.limit, search: search || undefined,
      status: appliedStatus === "all" ? undefined : appliedStatus } });
    const cur = (r.data.items || []).find((x) => x.id === id);
    if (cur) setSelected(cur);
  };

  const act = async (action) => {
    setBusy(true);
    try {
      const r = await api.put(`/t/${code}/applications/${selected.id}/stage`, { action, remarks });
      if (r.data.certificate_number) {
        toast.success(`Application completed — certificate ${r.data.certificate_number} issued`);
      } else {
        toast.success(action === "reject" ? "Application rejected" : `Moved to ${r.data.current_stage || "next stage"}`);
      }
      setSelected(null);
      setRemarks("");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const markFeePaid = async () => {
    setBusy(true);
    try {
      await api.put(`/t/${code}/applications/${selected.id}/mark-fee-paid`, { payment_mode: feeMode, reference: feeRef || null });
      toast.success("Fee marked as received");
      setFeeRef("");
      await refreshSelected(selected.id);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const downloadCert = async (a) => {
    try {
      await downloadBlob(`/t/${code}/applications/${a.id}/certificate`, `${(a.certificate_number || a.application_number).replace(/\//g, "-")}.pdf`);
    } catch {
      toast.error("Could not download certificate");
    }
  };

  return (
    <div>
      <PageHeader title="Service Applications" subtitle="Process citizen applications through workflow stages" />
      <FetchForm onFetch={gate.fetch} className="flex flex-wrap items-center gap-2 mb-4" testId="applications-filter-bar">
        <Input placeholder="Search application no., service, citizen…" value={q} onChange={(e) => setQ(e.target.value)}
          className="h-10 max-w-xs" data-testid="applications-search-input" />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-10 w-[160px]" data-testid="applications-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {APPLICATION_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <FetchButton type="submit" size="sm" loading={list.loading && gate.fetched} dirty={gate.dirty} fetched={gate.fetched} testId="applications-fetch-button" />
        {(q || statusFilter !== "all") && (
          <Button type="button" variant="ghost" size="sm" className="h-10" onClick={clearAll} data-testid="applications-clear-filters">Clear</Button>
        )}
        <FetchDirtyHint dirty={gate.dirty} testId="applications-fetch-dirty" />
      </FetchForm>
      {!gate.fetched ? (
        <Card className="shadow-sm"><CardContent className="p-0">
          <FetchPrompt onFetch={gate.fetch} testId="applications-fetch-prompt"
            subtitle="Search by application number, service or citizen, or pick a status above, then click Fetch to load the applications." />
        </CardContent></Card>
      ) : list.error && !items ? (
        <EmptyState title="Could not load applications" subtitle={errMsg(list.error)}
          action={<Button variant="outline" size="sm" onClick={list.reload} data-testid="applications-retry">Retry</Button>} />
      ) : !items ? <ListSkeleton /> : items.length === 0 ? (
        <EmptyState title={list.total === 0 && !search && appliedStatus === "all" ? "No applications yet" : "No applications match these filters"} />
      ) : (
        <div className={`space-y-3 ${list.loading ? "opacity-60 transition-opacity" : ""}`} data-testid="applications-admin-list">
          {items.map((a) => (
            <Card key={a.id} className="shadow-sm">
              <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-mono text-sm font-medium">{a.application_number}</p>
                    <StatusBadge status={a.status} />
                    <Badge variant="secondary" className="text-[10px] capitalize">{a.current_stage?.replace(/_/g, " ")}</Badge>
                    {a.payment_required && (
                      <Badge variant={a.payment_status === "paid" ? "secondary" : "destructive"} className="text-[10px]">
                        {a.payment_status === "paid" ? "Fee paid" : "Fee pending"}
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm mt-1 font-medium">{a.service_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {a.citizen_name} {a.applied_on_behalf ? `(via ${a.applied_by_name})` : ""} • {fmtDate(a.created_at)}
                    {a.payment_required ? ` • Fee ${fmtINR(a.payment_amount)}` : ""}
                    {(a.documents || []).length > 0 ? ` • ${a.documents.length} document(s)` : ""}
                  </p>
                </div>
                <div className="flex gap-2">
                  {a.status === "completed" && (
                    <Button size="sm" variant="outline" onClick={() => downloadCert(a)} data-testid={`application-certificate-${a.application_number}`}>
                      <Award className="h-3.5 w-3.5 mr-1.5" /> Certificate
                    </Button>
                  )}
                  {!["completed", "rejected", "cancelled"].includes(a.status) && (
                    <Button size="sm" onClick={() => setSelected(a)} data-testid={`application-process-${a.application_number}`}>Process</Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      {items && list.total > 0 && (
        <PaginationBar page={list.page} limit={list.limit} total={list.total} loading={list.loading}
          onPageChange={list.setPage} onLimitChange={list.setLimit} testId="applications-pagination" />
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading font-mono text-base">{selected?.application_number}</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="bg-muted rounded-lg p-3 text-sm space-y-1">
                {Object.entries(selected.form_data || {}).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-3">
                    <span className="text-muted-foreground capitalize">{k.replace(/_/g, " ")}</span>
                    <span className="font-medium text-right">{String(v)}</span>
                  </div>
                ))}
              </div>

              {(selected.documents || []).length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-1">Documents</p>
                  <div className="flex gap-2 flex-wrap">
                    {selected.documents.map((d, i) => (
                      <a key={i} href={fileHref(d.url)} target="_blank" rel="noreferrer"
                        className="text-xs flex items-center gap-1 border rounded-full px-2.5 py-1 hover:bg-muted"
                        data-testid={`admin-app-document-${i}`}>
                        <Paperclip className="h-3 w-3" /> {d.filename || `Document ${i + 1}`}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {selected.payment_required && (
                <div className="border rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium flex items-center gap-1.5"><IndianRupee className="h-4 w-4" /> Service fee {fmtINR(selected.payment_amount)}</p>
                    <Badge variant={selected.payment_status === "paid" ? "secondary" : "destructive"} className="text-[10px]" data-testid="admin-app-payment-status">
                      {selected.payment_status === "paid" ? `Paid • ${selected.payment_mode || ""}` : "Pending"}
                    </Badge>
                  </div>
                  {selected.payment_status !== "paid" && (
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <Select value={feeMode} onValueChange={setFeeMode}>
                        <SelectTrigger data-testid="fee-mode-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{["Cash", "UPI", "Card", "Cheque", "DD"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input placeholder="Reference (optional)" value={feeRef} onChange={(e) => setFeeRef(e.target.value)} data-testid="fee-reference-input" />
                      <Button variant="outline" onClick={markFeePaid} disabled={busy} data-testid="mark-fee-paid-button">Mark fee received</Button>
                    </div>
                  )}
                </div>
              )}

              <div>
                <p className="text-sm font-medium mb-1">Workflow</p>
                <div className="flex gap-1 flex-wrap">
                  {(selected.workflow_stages || []).map((s) => (
                    <Badge key={s} variant={s === selected.current_stage ? "default" : "outline"} className="capitalize text-[10px]">{s.replace(/_/g, " ")}</Badge>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-sm font-medium mb-1">History</p>
                <div className="space-y-1.5 max-h-32 overflow-y-auto">
                  {(selected.stage_history || []).map((h, i) => (
                    <p key={i} className="text-xs text-muted-foreground">
                      <span className="font-medium text-foreground capitalize">{h.action?.replace(/_/g, " ")} {h.stage?.replace(/_/g, " ")}</span> by {h.by} • {fmtDate(h.at)} {h.remarks ? `• ${h.remarks}` : ""}
                    </p>
                  ))}
                </div>
              </div>
              <div className="space-y-1">
                <Label>Remarks</Label>
                <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="application-remarks-input" />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="destructive" onClick={() => act("reject")} disabled={busy} data-testid="application-reject-button">Reject</Button>
                <Button onClick={() => act("advance")} disabled={busy} data-testid="application-advance-button">Advance to next stage</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
